import { Col, DatePicker, Flex, Row, Skeleton, Table, Typography } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUsage } from '../api/hooks';
import type { TenantUsage } from '../api/types';
import { formatNumber } from '../format';
import { StatCard } from './StatCard';

const fmt = (d: Dayjs) => d.format('YYYY-MM-DD');
const mb = (bytes: number) => `${formatNumber(bytes / 1_048_576, 1)} Mo`;

/**
 * Usage of a tenant over a period (FR-TRV-17): active sites and users, expéditions, documents
 * and files, in total and per site, for licence follow-up. `path` is the API resource.
 */
export function UsagePanel({ path }: { path: string }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(12, 'month').add(1, 'day'),
    dayjs(),
  ]);
  const usage = useUsage(path, fmt(period[0]), fmt(period[1]));
  const u = usage.data;
  const presets = [
    { label: t('usage.presets.year'), value: [dayjs().startOf('year'), dayjs()] },
    {
      label: t('usage.presets.twelveMonths'),
      value: [dayjs().subtract(12, 'month').add(1, 'day'), dayjs()],
    },
    { label: t('usage.presets.month'), value: [dayjs().startOf('month'), dayjs()] },
  ] as { label: string; value: [Dayjs, Dayjs] }[];

  return (
    <Flex vertical gap={16}>
      <Flex gap={8} align="center" wrap>
        <Typography.Text type="secondary">{t('usage.period')}</Typography.Text>
        <DatePicker.RangePicker
          value={period}
          presets={presets}
          onChange={(v) => v?.[0] && v[1] && setPeriod([v[0], v[1]])}
          format="DD/MM/YYYY"
          allowClear={false}
        />
      </Flex>
      {usage.isLoading || !u ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : (
        <>
          <Row gutter={[12, 12]}>
            <Col xs={12} md={6}>
              <StatCard
                label={t('usage.sites')}
                value={u.sites.withActivity}
                hint={t('usage.sitesHint', { active: u.sites.active, total: u.sites.total })}
              />
            </Col>
            <Col xs={12} md={6}>
              <StatCard
                label={t('usage.users')}
                value={u.users.active}
                hint={t('usage.usersHint', { total: u.users.total })}
              />
            </Col>
            <Col xs={12} md={6}>
              <StatCard
                label={t('usage.expeditions')}
                value={u.expeditions}
                hint={t('usage.chargementsHint', { count: u.chargements })}
              />
            </Col>
            <Col xs={12} md={6}>
              <StatCard
                label={t('usage.documents')}
                value={u.documents.count}
                hint={t('usage.filesHint', {
                  count: u.attachments.count,
                  size: mb(u.documents.bytes + u.attachments.bytes),
                })}
              />
            </Col>
          </Row>
          <Table<TenantUsage['perSite'][number]>
            rowKey="siteId"
            size="small"
            pagination={false}
            dataSource={u.perSite}
            rowClassName={(s) => (s.active ? '' : 'wm-row-inactive')}
            columns={[
              { title: t('organisation.site'), dataIndex: 'name' },
              {
                title: t('usage.columns.expeditions'),
                dataIndex: 'expeditions',
                align: 'end',
                width: 120,
              },
              {
                title: t('usage.columns.chargements'),
                dataIndex: 'chargements',
                align: 'end',
                width: 120,
              },
              {
                title: t('usage.columns.documents'),
                align: 'end',
                width: 160,
                render: (_, s) => `${s.documents} · ${mb(s.documentBytes)}`,
              },
            ]}
          />
        </>
      )}
    </Flex>
  );
}
